import { useCallback, useEffect, useMemo, useState } from "react";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "./BaseModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ComunicacionAjax } from "../../ts/api/comunicacion.js";
import { TypedTable } from "../TypedTable.js";
import { Constantes } from "../../ts/constantes.js";
import { notificacion, notificacionError, notificacionTipo } from "../Alerts.js";
import type { Respuesta } from "../../ts/typings/clases.js";
import { createColumnHelper } from "@tanstack/react-table";
import { useAppForm, useStore } from "../../hooks/useForm.js";
import { Input } from "../ui/input.js";
import z from "zod";
import { QueryError } from "../Loading/Loading.js";

interface ModalCrearOFProps extends BaseModalProps {
	readonly idArticulo: string | null;
}
export function ModalCrearOF(props: ModalCrearOFProps) {
	const queryClient = useQueryClient();

	const [tipoArticulo, setTipoArticulo] = useState<"OF" | "OPE" | "OF+OPE" | null>(null);
	const { data, error } = UseDatosFabricacion(props.idArticulo);
	const { data: puestos } = useAllPuestos();


	const form = useAppForm({
		defaultValues: {
			cantidadOrden: data?.cantidadOptima ?? "",
			inputPrioridad: 3,
			inputLote: "0",
			selectorProveedor: data?.defaultProveedor,
			selectorOPE: null,
			loteTexto: "",
			selectorMaquina: "",
		} as iInputsCrearOF,
		validators: {
			onSubmit: zInputsCrearOF,
		},
		onSubmit: async ({ value }) => {
			if (props.idArticulo == null) throw new Error("idArticulo no puede ser null");

			if (tipoArticulo === "OF+OPE") {
				if (value.selectorProveedor == "" || value.selectorProveedor == null) {
					notificacionError("Debe asignar un proveedor a OF con OPE");
					return;
				}

				if (value.selectorOPE == null) {
					notificacionError("Debe asignar una id de OPE");
					return;
				}
			}

			if (tipoArticulo == "OPE") {
				await lanzarOPE(props.idArticulo, value, props.close);
			} else if (tipoArticulo == "OF" || tipoArticulo == "OF+OPE") {
				if (!value.selectorMaquina) {
					notificacionError("Debe seleccionar una máquina.");
					return;
				}

				const ok = await lanzarOF(props.idArticulo, value, tipoArticulo, componentes, props.close);
				if (!ok) return;
			} else {
				notificacionError("Tipo de artículo no válido");
				console.error("Tipo de artículo no válido", tipoArticulo);
				return;
			}


			await queryClient.invalidateQueries({ queryKey: ["getOrdenesConjunto", { idConjunto: props.idArticulo }] });
			form.reset();
		},
	});

	const currentProveedor = useStore(form.store, (state) => state.values.selectorProveedor);
	const { data: OPEs, error: errorOPEs } = UseOPEsCompatibles(currentProveedor);


	const textoFases = useMemo(() => {
		return data?.ruta.fases.map((linea) => {
			if (linea.xcentro_id == "1") {
				return `${linea.xfaseruta_id} - ${linea.xdescripcion}`;
			} else if (linea.xcentro_id == "EXT") {	// tiene una ope
				return `${linea.xfaseruta_id} - CENTRO EXTERNO - ${linea.xdescripcion}`;
			} else {
				console.error("Ruta en almacen desconocido!");
				return "";
			}
		}) ?? [];
	}, [data]);


	useEffect(() => {
		if (data == null) return;

		switch (data.tipoArticulo) {
			case "OF":
				// modal.find("#tipoArticuloOrden").val("OF");
				// modal.find(".camposOF").show();
				if (data.ruta.fases.some(f => f.xcentro_id == "EXT"))
					setTipoArticulo("OF+OPE");
				else
					setTipoArticulo("OF");

				break;
			case "OPE":
				// modal.find("#tipoArticuloOrden").val("OPE");
				// modal.find(".camposOPE").show();.
				setTipoArticulo("OPE");
				break;
			case "Compras":
				// modal.find("#tipoArticuloOrden").val("Compras");
				if (data.ruta.fases.length > 0) alert("El articulo es de tipo compras pero tiene una ruta de fabricacion!");
				throw new Error("No se puede usar este modal para compras!");
			default:
				// modal.find("#tipoArticuloOrden").val(data.tipoArticulo);
				throw new Error(`Tipo de articulo (${data.tipoArticulo}) desconocido!`);
		}
	}, [data]);

	const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();
		void form.handleSubmit();
	}, [form]);

	useEffect(() => {
		form.reset();
	}, [form, props.idArticulo]);

	const proveedoresMap = useMemo(() => {
		const map: Record<string, string> = {};
		data?.proveedores.forEach(proveedor => {
			map[proveedor.idProveedor] = proveedor.nombre;
		});
		return map;
	}, [data?.proveedores]);
	const opesMap = useMemo(() => {
		const map: Record<string, string> = {
			"new": "Nueva OPE"
		};
		OPEs?.forEach(ope => {
			map[ope.idOPE] = `${ope.idOPE} - ${new Date(ope.fechaCreacion).toLocaleDateString()}`;
		});
		return map;
	}, [OPEs]);

	const [componentes, setComponentes] = useState<InfoComponente[]>(data?.componentes ?? []);
	useEffect(() => {
		setComponentes(data?.componentes ?? []);
	}, [data?.componentes]);

	if (error || errorOPEs) return <QueryError error={error ?? errorOPEs} fallback="Error al cargar los datos de fabricación" />;

	const showOPE = tipoArticulo === "OPE" || tipoArticulo === "OF+OPE";
	const showOF = tipoArticulo === "OF" || tipoArticulo === "OF+OPE";


	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Orden de Fabricación</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "60vw" }}>
				<div style={{ marginBottom: 20 }}>
					<h1 style={{ color: "#111", fontSize: 32, margin: 0 }} >
						{data?.descripcion ?? "..."}
					</h1>
					<h2 style={{ margin: 0 }}>
						<span>Conjunto:</span>
						<span style={{ color: "#666666", fontWeight: "bold" }} >
							{props.idArticulo}
						</span>
					</h2>
				</div>

				<form onSubmit={onSubmit} key={props.idArticulo ?? "no-articulo"}>
					<div className="cabeceraCrearOF">
						<form.AppField name="cantidadOrden">
							{field => (
								<field.NumberField
									label="Cantidad"
									placeholder=""
									min={1}
									autoComplete="fabrication-quantity"
								/>
							)}
						</form.AppField>

						<Input label="Tipo de Artículo" readOnly disabled value={tipoArticulo ?? ""} />

						<form.AppField name="inputPrioridad">
							{field => showOF && (
								<field.NumberField
									label="Prioridad"
									placeholder=""
									min={1} max={5}
									autoComplete="fabrication-priority"
								/>
							)}
						</form.AppField>

						<Input label="Lote Económico" readOnly disabled value={data?.cantidadOptima ?? ""} />

						<form.AppField name="inputLote">
							{field => showOPE && (
								<div>
									<label className="labelsForm">Acabado: </label>
									<field.SelectField
										isClearable
										options={{
											"0": "Normal",
											"AZUL": "Azul",
											"VERDE": "Verde",
											"ROJO": "Rojo",
										}}
									/>
								</div>
							)}
						</form.AppField>
					</div>

					{data?.hasLotes ? (
						<div className="section">
							<form.AppField name="loteTexto">
								{field => (
									<field.TextField
										label="Lote"
										placeholder="Introduce lote"
										autoComplete="fabrication-lote"
									/>
								)}
							</form.AppField>
						</div>
					) : null}


					<div className="cabeceraCrearOF" style={{ marginTop: 35, display: (showOPE || showOF) ? undefined : "none" }}>
						<form.AppField name="selectorProveedor">
							{field => showOPE && (
								<div>
									<label className="labelsForm">Proveedor: </label>
									<field.SelectField
										isClearable
										options={proveedoresMap}
									/>
								</div>
							)}
						</form.AppField>

						<form.AppField name="selectorOPE">
							{field => showOPE && (
								<div>
									<label className="labelsForm">OPE: </label>
									<field.SelectField
										isClearable
										options={opesMap}
									/>
								</div>
							)}
						</form.AppField>

						<form.AppField name="selectorMaquina">
							{field => (
								<div>
									<label className="labelsForm">Máquina</label>
									<field.SelectField
										options={{
											"": "Seleccione máquina...",
											...(puestos?.puestos.reduce((acc, p) => {
												acc[p.idPuesto] = `${p.idPuesto} - ${p.nombrePuesto}`;
												return acc;
											}, {} as Record<string, string>)),
										}}
										isClearable
									/>
								</div>
							)}
						</form.AppField>
					</div>
				</form>

				<div style={{ marginTop: 35 }}>
					<div style={{ display: (textoFases.length == 0 || !showOF) ? "none" : "contents" }}>
						<label className="labelsForm" style={{ width: "100%" }} >
							{`Ruta: ${data?.ruta.id} - ${data?.ruta.descripcion}`}
						</label>
						<textarea style={{ width: "100%", resize: "none", }} readOnly rows={5} cols={50} value={textoFases.join("\n")} />
					</div>

					<div className="table-responsive" style={{ marginTop: 20, borderBottom: "1px solid #111", display: !showOF ? "none" : "contents" }}>
						<label className="labelsForm" style={{ width: "100%" }} >Componentes</label>
						<TablaComponentes data={componentes} />
						{Constantes.elegirComponentesOF == true && (
							<FormComponente data={componentes} setData={setComponentes} />
						)}

					</div>
				</div>
			</div>
			<div className="modal-footer">
				<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				<button type="button" className="btnPrimary" onClick={() => void form.handleSubmit()}>Lanzar Orden</button>
			</div>
		</BaseModal >
	);
}





function TablaComponentes(props: { readonly data: InfoComponente[]; }) {
	// modal.on("click", ".eliminarComponente", function () {
	// 	const table = $(this).closest("table").DataTable();
	// 	const row = table.row($(this).closest("tr"));
	// 	const rowData = row.data() as InfoComponente;

	// 	if (addedComponentes.has(rowData.id)) {
	// 		addedComponentes.delete(rowData.id);
	// 	} else {
	// 		originalComponentes = originalComponentes.filter(comp => comp.id !== rowData.id);
	// 	}

	// 	const rowElement = $(this).closest("tr");
	// 	tablaComponentes.removeRow(rowElement);

	// 	notificacion("Componente eliminado correctamente");
	// });


	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<InfoComponente>();

		const getBtnEliminar = () => {
			return (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<button className="eliminarComponente" type="button">
						<i className="fa fa-trash" />
					</button>
				</div>
			);
		};

		return [
			columnHelper.accessor("referencia", { header: "ID", meta: { className: "center" }, cell: info => info.getValue() }),
			columnHelper.accessor("descComponente", { header: "Descripción", meta: { className: "center" } }),
			columnHelper.accessor("descFamilia", { header: "Familia", meta: { className: "center" } }),
			columnHelper.accessor("descSubfamilia", { header: "Subfamilia", meta: { className: "center", hidden: !Constantes.existenSubfamilias }, }),
			columnHelper.accessor("cantidadPorArticulo", {
				header: "Cantidad por conjunto", meta: { className: "center" }, cell: (info) => getFraccion(info.getValue()),
			}),
			columnHelper.display({
				id: "eliminar",
				header: "",
				meta: { className: "center", hidden: !Constantes.elegirComponentesOF },
				cell: getBtnEliminar,
			}),
		];
	}, []);

	return (
		<TypedTable
			// scrollY="400px"
			className="dataTable display"
			columns={columns}
			data={props.data}
		/>
	);
}

function FormComponente(props: { readonly data: InfoComponente[], readonly setData: Setter<InfoComponente[]>; }) {
	const [adding, setAdding] = useState(false);
	const form = useAppForm({
		defaultValues: {
			referencia: "",
			cantidadPorUnidad: null,
		} as iInputsAddComponente,
		validators: {
			onSubmit: zInputsAddComponente,
		},
		onSubmit: async ({ value }) => {
			if (value.cantidadPorUnidad == null || isNaN(value.cantidadPorUnidad) || value.cantidadPorUnidad <= 0) {
				notificacionError("La cantidad debe ser un número válido y mayor que 0.");
				return false;
			}

			const articulo = await getArticuloByReference(value.referencia);
			if (!articulo.exists) {
				notificacionError("La referencia de artículo no es válida o no se ha encontrado.");
				return false;
			}

			if (props.data.some(comp => comp.idComponente == articulo.id)) {
				notificacionError("Este componente ya ha sido agregado.");
				return;
			}
			const infoComponente: InfoComponente = {
				idComponente: articulo.id,
				referencia: articulo.referencia,
				descComponente: articulo.descripcion,
				descFamilia: articulo.nombreFamilia,
				descSubfamilia: articulo.nombreSubFamilia,
				cantidadPorArticulo: value.cantidadPorUnidad,
			};


			props.setData((prev) => [...prev, infoComponente]);

			form.reset();
			setAdding(false);
		},
	});
	const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();
		void form.handleSubmit();
	}, [form]);


	return (
		<div style={{ borderTop: "1px solid black" }} id="contenedorAddComponente">
			<div style={{ display: adding ? "none" : undefined }}>
				<button style={{ width: "100%", border: "none", padding: "8px 10px" }} type="button" onClick={() => setAdding(true)}>
					Añadir Componente
				</button>
			</div>
			<div style={{ display: !adding ? "none" : undefined }}>
				<form onSubmit={onSubmit} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px" }}>
					<form.AppField name="referencia">
						{field => (
							<label style={{ margin: 0, display: "flex", gap: 10, alignItems: "center" }}>
								<span>Referencia </span>
								<field.TextField />
							</label>
						)}
					</form.AppField>

					<form.AppField name="cantidadPorUnidad">
						{field => (
							<label style={{ margin: 0, display: "flex", gap: 10, alignItems: "center" }}>
								<span>Cantidad por unidad fabricada</span>
								<field.NumberField step="any" />
							</label>
						)}
					</form.AppField>

					<button type="submit">+</button>
				</form>
			</div>
		</div>
	);

}


async function lanzarOF(idConjunto: string, values: iInputsCrearOF, tipoOrden: string, componentes: InfoComponente[], onSuccess: () => void): Promise<boolean> {
	const parametro = {
		cantidad: values.cantidadOrden,
		idConjunto: idConjunto,
		idProveedor: values.selectorProveedor,
		prioridad: values.inputPrioridad,
		idOPE: values.selectorOPE,
		lote: values.loteTexto,
		addedComponentes: componentes.map(comp => ({
			id: comp.idComponente,
			cantidadPorArticulo: comp.cantidadPorArticulo,
		})),
	};

	if (parametro.addedComponentes.length === 0) {
		notificacionError("No se han añadido componentes a la OF.");
		return false;
	}

	const url = "/anadirOF";
	const response = await ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", url, parametro);
	if (response.tipo == "error") {
		notificacionTipo("error", response.message ?? "Error lanzando OF");
		return false;
	}

	if (response.tipo == "warning") {
		notificacionTipo("warning", response.message ?? "OF lanzada con advertencias");
		return false;
	}
	notificacionTipo("success", response.message ?? "OF lanzada correctamente");
	onSuccess();

	return true;
}
async function lanzarOPE(idConjunto: string, values: iInputsCrearOF, onSuccess: () => void): Promise<void> {
	const parametro = {
		idConjunto: idConjunto,

		idOPE: values.selectorOPE,
		idProveedor: values.selectorProveedor,

		lote: values.inputLote,
		cantidad: values.cantidadOrden,
	};

	if (parametro.cantidad == null || isNaN(parametro.cantidad)) {
		notificacionError("La cantidad no es un número válido");
		return;
	}
	if (parametro.idProveedor == "" || parametro.idProveedor == null) {
		notificacionError("Debe asignar un proveedor a OF con OPE");
		return;
	}
	if (parametro.idOPE == "" || parametro.idOPE == null) {
		notificacionError("Debe asignar una id de OPE");
		return;
	}

	console.log(parametro);


	const url = "/api/v1/anadirOPE";
	const response = await ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", url, parametro);

	if (response.tipo != "exito") {
		notificacionError(response.message ?? "Error añadiendo OPE");
		return;
	}

	notificacion(response.message ?? "OPE añadida correctamente");
	onSuccess();
}
function getFraccion(cantidadPorArticulo: number) {
	const formatter = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 8, minimumFractionDigits: 0 });
	if (cantidadPorArticulo < 1 && cantidadPorArticulo > -1) {
		return (
			<div style={{ textAlign: "center" }}>
				<span>1</span>
				<span>÷</span>
				<span>{Math.round(1 / cantidadPorArticulo)}</span>
				<span>=</span>
				<span>{formatter.format(cantidadPorArticulo)}</span>
			</div>
		);
	} else {
		return formatter.format(cantidadPorArticulo);
	}
}




function UseDatosFabricacion(idArticulo: string | null) {
	return useQuery<Conjunto | null>({
		queryKey: ["getDatosFabricacionArticulo", idArticulo],
		queryFn: () => {
			if (idArticulo == null) return Promise.resolve(null);
			return ComunicacionAjax.sendAjaxRequest<Conjunto>("GET", "/getDatosFabricacionArticulo", { "idconjunto": idArticulo, });
		}
	});
}
function UseOPEsCompatibles(idProveedor: string | null) {
	return useQuery<OPE[]>({
		queryKey: ["getOPEs", idProveedor],
		queryFn: () => {
			if (idProveedor == null) return Promise.resolve([]);
			return ComunicacionAjax.sendAjaxRequest<OPE[]>("GET", "/api/v1/getOPEs", { idProveedor });
		}
	});
}

function getArticuloByReference(referenciaArticulo: string): Promise<InfoArticulo> {
	const parametro = {
		"referencia": referenciaArticulo,
	};

	const url = "/api/v1/getDatosArticulo";
	return ComunicacionAjax.sendAjaxRequest<InfoArticulo>("GET", url, parametro);
}
function useAllPuestos() {
	return useQuery({
		queryKey: ["getAllPuestos"],
		queryFn: () =>
			ComunicacionAjax.sendAjaxRequest<{ puestos: Puesto[]; }>(
				"POST",
				"/api/v1/getAllPuestos",
				{}
			),
	});
}

interface Conjunto {
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- String seria un error, y quiero el autocompletado
	tipoArticulo: "OF" | "OPE" | "Compras" | "OF+OPE" | string;
	hasLotes: boolean;
	descripcion: string;
	cantidadOptima: number | null;
	defaultProveedor: string | null;
	componentes: InfoComponente[];
	proveedores: Proveedor[];
	ruta: {
		id: string,
		descripcion: string,
		fases: Fase[],
	};
}
interface Fase {
	xfaseruta_id: string;
	xdescripcion: string;
	xcentro_id: string;
}
interface InfoComponente {
	idComponente: number;
	referencia: string;
	descFamilia: string;
	descSubfamilia: string;
	descComponente: string;
	cantidadPorArticulo: number;
}
interface OPE {
	idOPE: number,
	fechaCreacion: number;
}
interface Puesto {
	idPuesto: string;
	nombrePuesto: string;
}
interface Proveedor {
	idProveedor: string,
	nombre: string;
}
type InfoArticulo = {
	exists: true,

	id: number,
	descripcion: string,
	referencia: string,
	peso: number,
	controlaLotes: boolean,

	nombreFamilia: string,
	nombreSubFamilia: string,
} | {
	exists: false,
};

const zInputsCrearOF = z.object({
	cantidadOrden: z.number().min(1, { message: "La cantidad debe ser mayor que 0" }).nullable(),

	inputPrioridad: z.number().min(1).max(5).nullable(),	// Optional porque no se muestra en OPE

	inputLote: z.string().nullable(),					// Optional porque no se muestra en OF
	selectorProveedor: z.string().nullable(),			// Optional porque no se muestra en OF
	selectorOPE: z.string().nullable(),	// Optional porque no se muestra en OF
	loteTexto: z.string().nullable(),
	selectorMaquina: z.string().nullable(),
});
type iInputsCrearOF = z.infer<typeof zInputsCrearOF>;


const zInputsAddComponente = z.object({
	referencia: z.string(),					// Optional porque no se muestra en OF
	cantidadPorUnidad: z.number().min(Number.EPSILON, { message: "La cantidad debe ser mayor que 0" }).nullable(),
});
type iInputsAddComponente = z.infer<typeof zInputsAddComponente>;
//Used in TablaComponentes
// async function getArticuloByReference(referenciaArticulo: string): Promise<InfoArticulo> {
// 	const parametro = {
// 		"referencia": referenciaArticulo,
// 	};

// 	const url = "/api/v1/getDatosArticulo";
// 	const response = await ComunicacionAjax.sendAjaxRequest("GET", url, parametro) as InfoArticulo;

// 	return response;
// }
// export type InfoArticulo = {
// 	exists: true,

// 	id: number,
// 	descripcion: string,
// 	referencia: string,
// 	peso: number,
// 	controlaLotes: boolean,

// 	nombreFamilia: string,
// 	nombreSubFamilia: string,
// } | {
// 	exists: false,

// 	id: null,
// 	referencia: null,
// 	peso: null,
// 	descripcion: null,
// 	controlaLotes: null,

// 	nombreFamilia: null,
// 	nombreSubFamilia: null,
// };
